import React, { useState, useCallback, memo } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";

// Notification Item Component
const NotificationItem = memo(({ notification, index }) => {
  const navigate = useNavigate();

  const extractContent = useCallback((msg) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(msg, "text/html");
      const anchor = doc.querySelector("a");
      if (!anchor) return { icon: null, text: msg, url: null };

      const icon = anchor.querySelector("i")?.outerHTML || null;
      const text = anchor.textContent.replace(icon || "", "").trim();
      const url = anchor.getAttribute("href");

      // Check if URL is empty or javascript:void(0)
      const isValidUrl = url && !url.startsWith('javascript:') && url !== '#';
      
      return { 
        icon, 
        text, 
        url: isValidUrl ? url : null 
      };
    } catch (error) {
      console.error("Error parsing notification:", error);
      return { icon: null, text: msg, url: null };
    }
  }, []);

  const { icon, text, url } = extractContent(notification.Msg);

  const handleClick = useCallback((e) => {
    if (url) {
      e.preventDefault();
      navigate(url);
    }
    // Else do nothing
  }, [navigate, url]);

  const isClickable = !!url;

  return (
    <div
      onClick={isClickable ? handleClick : undefined}
      style={{
        display: "flex",
        alignItems: "center",
        padding: "8px 0",
        borderBottom: "1px dashed #ABABAB",
        cursor: isClickable ? "pointer" : "default",
        transition: "all 0.2s ease",
        fontSize: "11px",
        color: "#141468",
        ...(!isClickable && {
          opacity: 0.8 // Optional: visually distinguish non-clickable items
        })
      }}
    >
      {/* Icon Container - Only show if icon exists */}
      {icon && (
        <div
          style={{
            width: "20px",
            height: "20px",
            backgroundColor: "#DFEBFF",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: "12px",
            fontSize: "12px",
            color: "#141468",
          }}
        >
          <div dangerouslySetInnerHTML={{ __html: icon }} />
        </div>
      )}

      {/* Text Content */}
      <div style={{ flex: 1 }}>
        <span
          style={{
            fontSize: "11px",
            color: "#495057",
            fontWeight: "500",
            lineHeight: "1.4",
          }}
        >
          {text}
        </span>
      </div>
    </div>
  );
});

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    Msg: PropTypes.string.isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
};

// Main AlertBox component (fallback for non-notification messages)
const AlertBox = memo(({ message, index }) => {
  const extractUrl = useCallback((msg) => {
    const match = msg?.match(/redirectToPage\('([^']+)'\)/);
    return match?.[1] ?? null;
  }, []);

  // FIX: Check if message contains multiple notifications (has <br/> tags)
  const containsMultipleNotifications = message?.includes('<br/>');
  
  // If it contains multiple notifications, split and render each one
  if (containsMultipleNotifications) {
    // Split by <br/> and filter out empty strings
    const notifications = message.split('<br/>').filter(n => n && n.trim() !== '');
    
    // Return a fragment with all notifications
    return (
      <>
        {notifications.map((notificationMsg, idx) => {
          const isNotification = notificationMsg?.includes('<i class="') && 
                               notificationMsg?.includes("</i>");

          if (isNotification) {
            return (
              <NotificationItem 
                key={`notification-${index}-${idx}`} 
                notification={{ Msg: notificationMsg }} 
                index={idx} 
              />
            );
          }

          return (
            <div
              key={`alert-${index}-${idx}`}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "12px 0",
                borderBottom: "1px dotted #dee2e6",
                fontSize: "11px",
              }}
            >
              <div style={{ flex: 1 }}>
                <span
                  style={{
                    fontSize: "11px",
                    color: "#495057",
                    fontWeight: "500",
                    lineHeight: "1.4",
                  }}
                  dangerouslySetInnerHTML={{ __html: notificationMsg }}
                />
              </div>
            </div>
          );
        })}
      </>
    );
  }

  // Original logic for single notification
  const url = extractUrl(message);
  const isNotification =
    message?.includes('<i class="') && message?.includes("</i>");

  if (isNotification) {
    return <NotificationItem notification={{ Msg: message }} index={index} />;
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        padding: "12px 0",
        borderBottom: "1px dotted #dee2e6",
        fontSize: "11px",
      }}
    >
      <div style={{ flex: 1 }}>
        <span
          style={{
            fontSize: "11px",
            color: "#495057",
            fontWeight: "500",
            lineHeight: "1.4",
          }}
          dangerouslySetInnerHTML={{ __html: message }}
        />
      </div>
    </div>
  );
});

AlertBox.propTypes = {
  message: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
};

// Main AlertSection component
const AlertSection = ({ alerts }) => {
  const [isOpen, setIsOpen] = useState(true);

  const toggleAccordion = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  return (
    <div className="row">
      <div className="col-md-12 dashKPI">
        <div className="custom-accordion">
          <div
            className="custom-header"
            onClick={toggleAccordion}
            role="button"
            tabIndex={0}
            aria-expanded={isOpen}
          >
            <h6 className="mb-0 d-flex align-items-center">Alerts</h6>
            <span className={`arrow-icon ${isOpen ? "open" : ""}`}>
              <i className="bi bi-arrow-down-circle-fill icon-acc"></i>
            </span>
          </div>

          {/* Body */}
          {isOpen && (
            <div
              style={{
                padding: "0px 10px",
                backgroundColor: "white",
                borderRadius: "0 0 12px 12px",
                borderTop: "1px solid #e9ecef",
              }}
            >
              <div className={`custom-body ${isOpen ? "open" : "closed"}`}>
                {alerts.length > 0 ? (
                  alerts.map((item, index) => (
                    <AlertBox
                      key={`alert-${index}`}
                      message={item.Msg}
                      index={index}
                    />
                  ))
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 20px",
                      color: "#6c757d",
                      fontSize: "11px",
                    }}
                  >
                    No alerts available
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

AlertSection.propTypes = {
  alerts: PropTypes.arrayOf(
    PropTypes.shape({
      Msg: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default memo(AlertSection);